import {
  Box,
  Button,
  ButtonProps,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { StatCard, STAT_CARD_MIN_HEIGHT } from "components/StatCard";
import { StatusChip } from "components/StatusChip";
import {
  InvoiceAuditIssueFragment,
  InvoiceAuditSummaryFragment,
  WasteInvoiceChargeAuditResult,
} from "generated-graphql/graphql";
import { ReactNode } from "react";

import { StatCardGrid } from "../../components/StatCardGrid";
import { AUDIT_RESULT_LABELS, formatIssueLabel, formatAuditMoney } from "./formatAuditValues";

export function UnavailableButton({ children, ...props }: ButtonProps) {
  return (
    <Tooltip title="Not available yet">
      <span>
        <Button {...props} disabled>
          {children}
        </Button>
      </span>
    </Tooltip>
  );
}

export function AuditResult({ result }: { result: WasteInvoiceChargeAuditResult }) {
  return (
    <StatusChip
      label={AUDIT_RESULT_LABELS[result]}
      title={AUDIT_RESULT_LABELS[result]}
      color={
        result === "RATE_MISMATCH"
          ? "error"
          : result === "MATCH"
            ? "success"
            : result === "NOT_EVALUATED"
              ? "default"
              : "warning"
      }
      sx={{ maxWidth: "100%" }}
    />
  );
}

export function AuditFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {children ?? "—"}
      </Typography>
    </Box>
  );
}

export function AuditSummary({
  summary,
  detail = false,
}: {
  summary?: InvoiceAuditSummaryFragment;
  detail?: boolean;
}) {
  if (!summary)
    return (
      <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
        <StatCardGrid>
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} variant="rounded" height={STAT_CARD_MIN_HEIGHT} />
          ))}
        </StatCardGrid>
      </Paper>
    );
  const overages = formatAuditMoney(
    "variance",
    summary.calculatedLineCount > 0 ? summary.totalOverages : null
  );
  const metrics = detail
    ? [
        {
          label: "Total billed amount",
          value: formatAuditMoney("billedAmount", summary.billedAmount),
        },
        { label: "Total overages", value: overages },
        { label: "Line items", value: summary.lineCount.toLocaleString() },
        { label: "Rate mismatch lines", value: summary.nonCompliantLineCount.toLocaleString() },
      ]
    : [
        {
          label: "Total billed amount",
          value: formatAuditMoney("billedAmount", summary.billedAmount),
        },
        { label: "Total overages", value: overages },
        { label: "Total invoice lines", value: summary.lineCount.toLocaleString() },
        {
          label: "Total rate mismatch lines",
          value: summary.nonCompliantLineCount.toLocaleString(),
        },
      ];
  return (
    <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
      <StatCardGrid testId="audit-summary-cards">
        {metrics.map(({ label, value }) => (
          <StatCard key={label} caption={label} value={value} captionPlacement="top" />
        ))}
      </StatCardGrid>
    </Paper>
  );
}

export function AuditIssues({ issues }: { issues: readonly InvoiceAuditIssueFragment[] }) {
  return (
    <Stack spacing={1}>
      {issues.map((issue) => (
        <Box key={issue.key}>
          <Typography variant="body2" fontWeight={600}>
            {formatIssueLabel(issue.code)}
          </Typography>
          <Typography variant="body2">{issue.message}</Typography>
          <Typography variant="caption" color="text.secondary">
            {issue.blocksRateCalculation
              ? "Prevents rate calculation"
              : "Does not prevent a complete pre-fee calculation"}
          </Typography>
          {issue.rateCardEntryIds.length > 0 && (
            <Typography variant="caption" display="block" sx={{ overflowWrap: "anywhere" }}>
              Contract rate references: {issue.rateCardEntryIds.join(", ")}
            </Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
}
